import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Quote } from "lucide-react";
import { mediaUrl } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useBi } from "../../lib/customer";
import { Reveal, SectionHead } from "../Common";
import PropertyCard from "../PropertyCard";
import { ArticleCard, ProjectCard } from "../Cards";
import { ViewAll } from "./Kit";

export function Tabs({ tabs }) {
  const { t } = useI18n();
  const keys = ["residential", "commercial", "luxury", "mixed_use"];
  const [active, setActive] = useState("residential");
  const link = { residential: "/residential", commercial: "/commercial", luxury: "/properties?tag=luxury", mixed_use: "/properties?tag=mixed_use" }[active];
  return (
    <section data-testid="home-tabs" className="container-lux py-24">
      <SectionHead eyebrow={t("home.tabsTitle")} title={t("home.tabsTitle")} action={<ViewAll to={link} testid="home-tabs-view-all" />} />
      <div role="tablist" className="mb-10 flex flex-wrap gap-2">
        {keys.map((k) => (<button key={k} role="tab" aria-selected={active === k} data-testid={`home-tab-${k}`} onClick={() => setActive(k)} className={`chip ${active === k ? "chip-on" : ""}`}>{t(`home.tabs.${k}`)}</button>))}
      </div>
      {tabs[active]?.length ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid={`home-tab-panel-${active}`}>
          {tabs[active].slice(0, 6).map((p, i) => (<Reveal key={p.id} delay={i * 0.06}><PropertyCard p={p} /></Reveal>))}
        </div>
      ) : <p className="text-fg/50">{t("home.noItems")}</p>}
    </section>
  );
}

export function Stats({ stats }) {
  const { t, tr } = useI18n();
  if (!stats?.length) return null;
  return (
    <section data-testid="home-stats" className="border-y border-cta/15 bg-brand/30 py-20">
      <div className="container-lux">
        <p className="eyebrow mb-10">{t("home.stats")}</p>
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4">{stats.map((s) => (<div key={s.label_en}><p className="font-display text-5xl text-cta">{s.value}</p><p className="mt-2 text-sm text-fg/60">{tr(s, "label")}</p></div>))}</div>
      </div>
    </section>
  );
}

export function Social({ testimonials, partners }) {
  const { t, tr } = useI18n();
  return (
    <>
      {testimonials?.length > 0 && (
        <section data-testid="home-testimonials" className="container-lux py-24">
          <SectionHead eyebrow={t("home.testimonials")} title={t("home.testimonials")} />
          <div className="grid gap-6 md:grid-cols-3">{testimonials.map((x) => (<figure key={x.id} className="glass rounded-sm p-8"><Quote className="h-6 w-6 text-cta" /><blockquote className="mt-4 font-display text-xl">{tr(x, "quote")}</blockquote><figcaption className="mt-4 text-sm text-fg/60">{tr(x, "name")}</figcaption></figure>))}</div>
        </section>
      )}
      {partners?.length > 0 && (
        <section data-testid="home-partners" className="container-lux py-16">
          <p className="eyebrow mb-8">{t("home.partners")}</p>
          <div className="flex flex-wrap items-center gap-10">{partners.map((x) => (<img key={x.id} src={mediaUrl(x.logo_url)} alt={x.name} className="h-10 opacity-70 grayscale hover:opacity-100" />))}</div>
        </section>
      )}
    </>
  );
}

function CalcTeaser() {
  const b = useBi();
  return (
    <Reveal className="mt-10">
      <Link to="/investment" data-testid="home-calc-teaser" className="group grid items-center gap-6 rounded-sm border border-cta/25 bg-surface p-8 transition-[border-color] duration-300 hover:border-cta md:grid-cols-[auto_1fr_auto] md:p-10">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cta/15"><Calculator className="h-6 w-6 text-cta" /></span>
        <div><h3 className="font-display text-2xl text-fg">{b("Model your investment", "خطّط لاستثمارك")}</h3><p className="mt-1 text-sm text-fg/60">{b("Try different prices, rents and holding periods in our investment calculator. Estimates only — not financial advice.", "جرّب أسعاراً وإيجارات ومدد احتفاظ مختلفة في حاسبة الاستثمار. تقديرات فقط وليست نصيحة مالية.")}</p></div>
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-cta">{b("Open calculator", "افتح الحاسبة")}<ArrowRight className="rtl-flip h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
      </Link>
    </Reveal>
  );
}

export function Upcoming({ projects, investment }) {
  const { t } = useI18n();
  return (
    <section data-testid="home-upcoming" className="container-lux py-24">
      <SectionHead eyebrow={t("home.upcoming")} title={t("home.upcoming")} action={<ViewAll to="/developments" testid="home-upcoming-view-all" />} />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">{projects.map((p, i) => (<Reveal key={p.id} delay={i * 0.06}><ProjectCard p={p} /></Reveal>))}</div>
      <div className="mt-24">
        <SectionHead eyebrow={t("nav.investment")} title={t("home.investTitle")} action={<ViewAll to="/investment" testid="home-investment-view-all" />} />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{investment.map((p, i) => (<Reveal key={p.id} delay={i * 0.06}><PropertyCard p={p} /></Reveal>))}</div>
        <CalcTeaser />
      </div>
    </section>
  );
}

export function NewsRow({ articles }) {
  const { t } = useI18n();
  if (!articles?.length) return null;
  return (
    <section data-testid="home-news" className="container-lux py-24">
      <SectionHead eyebrow={t("home.news")} title={t("home.news")} sub={t("home.newsSub")} action={<ViewAll to="/news" testid="home-news-view-all" />} />
      <div className="grid gap-6 md:grid-cols-3">{articles.slice(0, 3).map((a, i) => (<Reveal key={a.id} delay={i * 0.08}><ArticleCard a={a} /></Reveal>))}</div>
    </section>
  );
}
